import { useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import DateTimePicker from 'react-datetime';
import moment from 'moment';
import _ from 'lodash';

import {
  ChainedOfferEventStatus,
  DeleteSongpopLegacyPackDropMutation,
  DeleteSongpopLegacyPackDropMutationVariables,
  ForceSendSongpopLegacyPackPushMutation,
  ForceSendSongpopLegacyPackPushMutationVariables,
  GetSpendTierAudienceQuery,
  GetSpendTierAudienceQueryVariables,
  SongpopLegacyPackNotifAnchor,
  SongpopLegacyPackNotifScheduleEntryInput,
  UpdateSongpopLegacyPackDropMutation,
  UpdateSongpopLegacyPackDropMutationVariables,
  UserSpendTier,
  UserSubscriptionTier,
} from '../../../../../../__gqltypes__';
import { SPEND_TIER_AUDIENCE_QUERY } from '../../SaleEvent/graphql';
import { applyChangeIfDateValid } from '../../../../../../utils/datepicker';
import {
  DELETE_SONGPOP_LEGACY_PACK_DROP,
  FORCE_SEND_SONGPOP_LEGACY_PACK_PUSH,
  UPDATE_SONGPOP_LEGACY_PACK_DROP,
} from '../graphql';
import {
  APP_VERSION_OPERATORS,
  dropStatusBadge,
  LegacyPackDrop,
  LegacyPackSkuOption,
  skuOptionLabel,
  textToUserIds,
  toRewardInput,
  userIdsToText,
} from '../utils';
import { PushAudienceModal, pushAudienceSummary, pushFiltersOf, SPEND_TIER_OPTIONS } from './PushAudienceModal';

const ALL_TIERS = 'All';

type Props = {
  drop: LegacyPackDrop;
  skuOptions: LegacyPackSkuOption[];
  onSaved: (drop: LegacyPackDrop) => void;
  onDeleted: (dropId: string) => void;
  // Every other sale config, to warn about two ACTIVE ones selling the same price point at once.
  otherDrops: LegacyPackDrop[];
};

// Same limits as the backend (admin/drops.ts).
const MAX_NOTIF_TITLE_LENGTH = 50;
const MAX_NOTIF_BODY_LENGTH = 180;

// Display only: the store charges the price point. Same options as the sale bundles.
const DISCOUNT_OPTIONS = [
  { name: 'None', value: 0 },
  ...[10, 20, 30, 40, 50, 60, 70, 80, 90].map((value) => ({ name: `${value}%`, value })),
];

function computeOldPrice(newPrice: number | undefined, discount: number): number | null {
  return discount && newPrice ? Number(((newPrice * 100) / (100 - discount)).toFixed(2)) : null;
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <Card className="mb-4 shadow-sm">
      <Card.Header className="bg-light text-center">
        <h5 className="mb-0">{title}</h5>
        {hint && <small className="text-muted">{hint}</small>}
      </Card.Header>
      <Card.Body>{children}</Card.Body>
    </Card>
  );
}

const ALL_SPEND_TIERS_COUNT = SPEND_TIER_OPTIONS.length;

// The pushes of a stored drop, in the input shape the form edits (with each push's matchers).
/**
 * "N hours after the event starts" = START +N, "N hours before the event ends" = STOP -N:
 * the admin only types the number of hours, the sign follows the anchor.
 */
const pushTiming = (anchor: SongpopLegacyPackNotifAnchor, hours: unknown) => {
  const amount = Math.abs(Number(hours) || 0);
  return { anchor, offsetHours: anchor === SongpopLegacyPackNotifAnchor.START ? amount : -amount };
};

// The default texts the server uses when a push has no custom title / text (notifications.ts).
const defaultTextName = (entry: SongpopLegacyPackNotifScheduleEntryInput) =>
  entry.anchor === SongpopLegacyPackNotifAnchor.START ? 'sale started' : 'ending soon';

/** Sent state of a stored push (a push not saved yet shows as waiting). */
function PushStatus({ stored }: { stored?: LegacyPackDrop['notifSchedule'][number] }) {
  if (stored?.sent) {
    return <Badge variant="success">{stored.repeatEveryHours ? 'All sent' : 'Sent'}</Badge>;
  }
  if (!stored?.repeatEveryHours) {
    return <Badge variant="secondary">Waiting</Badge>;
  }
  return (
    <>
      <Badge variant="info">Repeating</Badge>
      {stored.lastSentAt && (
        <small className="d-block text-muted">last {moment(stored.lastSentAt).format('MM-DD HH:mm')}</small>
      )}
      {stored.nextFireDate && (
        <small className="d-block text-muted">next {moment(stored.nextFireDate).format('MM-DD HH:mm')}</small>
      )}
    </>
  );
}

const scheduleFromDrop = (drop: LegacyPackDrop): SongpopLegacyPackNotifScheduleEntryInput[] =>
  drop.notifSchedule.map((entry) => ({
    anchor: entry.anchor,
    offsetHours: entry.offsetHours,
    title: entry.title,
    body: entry.body,
    repeatEveryHours: entry.repeatEveryHours ?? null,
    ...pushFiltersOf(entry),
  }));
const optionalNumber = (value: string) => (value.trim() === '' ? null : Number(value));

// Raw form values of a stored drop, in the shape the form state holds them.
function formValuesFromDrop(drop: LegacyPackDrop) {
  return {
    name: drop.name,
    status: drop.status,
    startDate: new Date(drop.startDate),
    stopDate: new Date(drop.stopDate),
    // true = the cap applies, null/false = no cap (the default)
    capEnabled: !!drop.capEnabled,
    capSeats: String(drop.capSeats),
    skuId: drop.skuId,
    offerPercentage: drop.offerPercentage?.toString() ?? '',
    rewards: drop.instantRewards.map(toRewardInput),
    schedule: scheduleFromDrop(drop),
    filterUserIds: userIdsToText(drop.filterUserIds),
    filterSubscriptionTier: (drop.filterSubscriptionTier as string) ?? ALL_TIERS,
    filterMinCreation: drop.filterUserMinCreationDate ? new Date(drop.filterUserMinCreationDate) : undefined,
    filterMaxCreation: drop.filterUserMaxCreationDate ? new Date(drop.filterUserMaxCreationDate) : undefined,
    filterAppVersion: drop.filterAppVersion ?? '',
    filterAppVersionOperator: drop.filterAppVersionOperator ?? '>=',
    filterUserLevel: drop.filterUserLevel?.toString() ?? '',
    filterExcludedUserIds: userIdsToText(drop.filterExcludedUserIds),
    filterHadSub: !!drop.filterHadSub,
    filterSpendTiers: (drop.filterSpendTiers ?? []) as UserSpendTier[],
  };
}
type FormValues = ReturnType<typeof formValuesFromDrop>;

// The mutation input for some form values. Also used to tell whether anything changed: the form
// is dirty exactly when this differs from the input of the stored drop.
function buildDropInput(id: string, values: FormValues, skuOptions: LegacyPackSkuOption[]) {
  const discount = Number(values.offerPercentage) || 0;
  const cost = skuOptions.find((option) => option.id === values.skuId)?.cost;
  const appVersion = values.filterAppVersion.trim();
  const spendTiers = values.filterSpendTiers;
  return {
    id,
    name: values.name,
    status: values.status,
    startDate: values.startDate,
    stopDate: values.stopDate,
    // off is stored as null (same meaning), so toggling on then off again is not a change
    capEnabled: values.capEnabled ? true : null,
    // Kept while the cap is off, so switching it back on finds the same number.
    capSeats: Number(values.capSeats),
    skuId: values.skuId,
    // Same as the sale bundles: only the discount is picked, the crossed-out price follows from it.
    priceUsdBase: computeOldPrice(cost, discount),
    offerPercentage: discount || null,
    instantRewards: values.rewards.map(({ type, amount, itemId }) => ({ type, amount, itemId: itemId ?? undefined })),
    notifSchedule: values.schedule.map((entry) => ({
      anchor: entry.anchor,
      offsetHours: Number(entry.offsetHours),
      title: entry.title?.trim() || null,
      body: entry.body?.trim() || null,
      repeatEveryHours: entry.repeatEveryHours ? Number(entry.repeatEveryHours) : null,
      ...pushFiltersOf(entry),
    })),
    filterUserIds: textToUserIds(values.filterUserIds),
    filterSubscriptionTier:
      values.filterSubscriptionTier === ALL_TIERS ? undefined : (values.filterSubscriptionTier as UserSubscriptionTier),
    filterUserMinCreationDate: values.filterMinCreation,
    filterUserMaxCreationDate: values.filterMaxCreation,
    filterAppVersion: appVersion || undefined,
    filterAppVersionOperator: appVersion ? values.filterAppVersionOperator : undefined,
    filterUserLevel: optionalNumber(values.filterUserLevel) ?? undefined,
    filterExcludedUserIds: textToUserIds(values.filterExcludedUserIds),
    filterHadSub: values.filterHadSub || undefined,
    filterSpendTiers: spendTiers.length > 0 && spendTiers.length < ALL_SPEND_TIERS_COUNT ? spendTiers : undefined,
  };
}

// Dates compared by instant, key order and tier order ignored.
function sameInput(a: ReturnType<typeof buildDropInput>, b: ReturnType<typeof buildDropInput>): boolean {
  const normalize = (input: ReturnType<typeof buildDropInput>) =>
    JSON.parse(
      JSON.stringify({ ...input, filterSpendTiers: input.filterSpendTiers && [...input.filterSpendTiers].sort() })
    );
  return _.isEqual(normalize(a), normalize(b));
}

/**
 * One drop = one sale window. It only controls who SEES the offer: the purchase itself never
 * checks cap, dates or status, so seatsSold can go above capSeats (accepted oversell): a valid
 * payment is always granted, even after the cap or the stop date.
 * Beta and prod share this data: saving here is live for both builds.
 */
export function DropData({ drop, skuOptions, onSaved, onDeleted, otherDrops }: Props) {
  const [name, setName] = useState(drop.name);
  const [status, setStatus] = useState(drop.status);
  const [startDate, setStartDate] = useState<Date>(new Date(drop.startDate));
  const [stopDate, setStopDate] = useState<Date>(new Date(drop.stopDate));
  const [capEnabled, setCapEnabled] = useState(!!drop.capEnabled);
  const [capSeats, setCapSeats] = useState(String(drop.capSeats));
  const [skuId, setSkuId] = useState(drop.skuId);
  const [offerPercentage, setOfferPercentage] = useState(drop.offerPercentage?.toString() ?? '');
  const [schedule, setSchedule] = useState<SongpopLegacyPackNotifScheduleEntryInput[]>(scheduleFromDrop(drop));
  // The push whose audience is being edited (index in `schedule`)
  const [audiencePushIndex, setAudiencePushIndex] = useState<number | null>(null);

  const [filterUserIds, setFilterUserIds] = useState(userIdsToText(drop.filterUserIds));
  const [filterSubscriptionTier, setFilterSubscriptionTier] = useState<string>(
    drop.filterSubscriptionTier ?? ALL_TIERS
  );
  const [filterMinCreation, setFilterMinCreation] = useState<Date | undefined>(
    drop.filterUserMinCreationDate ? new Date(drop.filterUserMinCreationDate) : undefined
  );
  const [filterMaxCreation, setFilterMaxCreation] = useState<Date | undefined>(
    drop.filterUserMaxCreationDate ? new Date(drop.filterUserMaxCreationDate) : undefined
  );
  const [filterAppVersion, setFilterAppVersion] = useState(drop.filterAppVersion ?? '');
  const [filterAppVersionOperator, setFilterAppVersionOperator] = useState(drop.filterAppVersionOperator ?? '>=');
  const [filterUserLevel, setFilterUserLevel] = useState(drop.filterUserLevel?.toString() ?? '');
  const [filterExcludedUserIds, setFilterExcludedUserIds] = useState(userIdsToText(drop.filterExcludedUserIds));
  const [filterHadSub, setFilterHadSub] = useState(!!drop.filterHadSub);
  const [filterSpendTiers, setFilterSpendTiers] = useState<UserSpendTier[]>(drop.filterSpendTiers ?? []);

  // Explicit action, as in the sale events: the count scans every payer row.
  const [loadSpendTierAudience, { data: spendTierAudienceData, loading: spendTierAudienceLoading }] = useLazyQuery<
    GetSpendTierAudienceQuery,
    GetSpendTierAudienceQueryVariables
  >(SPEND_TIER_AUDIENCE_QUERY, { fetchPolicy: 'no-cache' });
  const spendTierAudienceByTier = useMemo(() => {
    const rows = spendTierAudienceData?.liveOps.spendTierAudience ?? [];
    return rows.reduce<Record<string, typeof rows[number]>>((acc, row) => ({ ...acc, [row.tier]: row }), {});
  }, [spendTierAudienceData]);
  const spendTierComputedAt = spendTierAudienceData?.liveOps.spendTierAudience?.find(
    (row) => row.computedAt
  )?.computedAt;
  // Not evaluable over the whole population (request header / composite tables): still enforced at runtime.
  const audienceIgnoredMatchers = [
    filterAppVersion.trim() ? 'app version' : undefined,
    filterSubscriptionTier !== ALL_TIERS ? 'subscription tier' : undefined,
  ].filter((matcher): matcher is string => !!matcher);
  // Every tier ticked is saved as "All users": against an explicit list, a payer whose tier has not
  // synced yet would fail closed.
  const allSpendTiersSelected = filterSpendTiers.length === SPEND_TIER_OPTIONS.length;
  const toggleSpendTier = (tier: UserSpendTier) =>
    setFilterSpendTiers((old) => (old.includes(tier) ? old.filter((t) => t !== tier) : [...old, tier]));

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    // New drop selected: reset the form to its stored values.
    setName(drop.name);
    setStatus(drop.status);
    setStartDate(new Date(drop.startDate));
    setStopDate(new Date(drop.stopDate));
    setCapEnabled(!!drop.capEnabled);
    setCapSeats(String(drop.capSeats));
    setSkuId(drop.skuId);
    setOfferPercentage(drop.offerPercentage?.toString() ?? '');
    setSchedule(scheduleFromDrop(drop));
    setFilterUserIds(userIdsToText(drop.filterUserIds));
    setFilterSubscriptionTier(drop.filterSubscriptionTier ?? ALL_TIERS);
    setFilterMinCreation(drop.filterUserMinCreationDate ? new Date(drop.filterUserMinCreationDate) : undefined);
    setFilterMaxCreation(drop.filterUserMaxCreationDate ? new Date(drop.filterUserMaxCreationDate) : undefined);
    setFilterAppVersion(drop.filterAppVersion ?? '');
    setFilterAppVersionOperator(drop.filterAppVersionOperator ?? '>=');
    setFilterUserLevel(drop.filterUserLevel?.toString() ?? '');
    setFilterExcludedUserIds(userIdsToText(drop.filterExcludedUserIds));
    setFilterHadSub(!!drop.filterHadSub);
    setFilterSpendTiers(drop.filterSpendTiers ?? []);
  }, [drop]);

  const [updateDrop, { loading: saving, error: saveError }] = useMutation<
    UpdateSongpopLegacyPackDropMutation,
    UpdateSongpopLegacyPackDropMutationVariables
  >(UPDATE_SONGPOP_LEGACY_PACK_DROP, {
    onCompleted: ({ updateSongpopLegacyPackDrop }) => onSaved(updateSongpopLegacyPackDrop),
  });
  const [deleteDrop, { loading: deleting, error: deleteError }] = useMutation<
    DeleteSongpopLegacyPackDropMutation,
    DeleteSongpopLegacyPackDropMutationVariables
  >(DELETE_SONGPOP_LEGACY_PACK_DROP, {
    onCompleted: ({ deleteSongpopLegacyPackDrop }) => onDeleted(deleteSongpopLegacyPackDrop),
  });

  // "Send now" of one saved push: the index of the push waiting for confirmation, then the outcome.
  const [forcePushIndex, setForcePushIndex] = useState<number | null>(null);
  const [forcedPushIndex, setForcedPushIndex] = useState<number | null>(null);
  const [forceSendPush, { loading: forceSending, error: forceSendError }] = useMutation<
    ForceSendSongpopLegacyPackPushMutation,
    ForceSendSongpopLegacyPackPushMutationVariables
  >(FORCE_SEND_SONGPOP_LEGACY_PACK_PUSH, { refetchQueries: ['getSongpopLegacyPackDrops'] });

  const statusBadge = dropStatusBadge(drop);
  // One live event at a time (the backend refuses the save): shown before saving.
  const overlapping =
    status === ChainedOfferEventStatus.ACTIVE
      ? otherDrops.filter(
          (other) =>
            other.id !== drop.id &&
            other.status === ChainedOfferEventStatus.ACTIVE &&
            new Date(other.startDate) < stopDate &&
            startDate < new Date(other.stopDate)
        )
      : [];
  const selectedSku = skuOptions.find((option) => option.id === skuId);
  const discount = Number(offerPercentage) || 0;
  const oldPrice = computeOldPrice(selectedSku?.cost, discount);

  const currentInput = buildDropInput(
    drop.id,
    {
      name,
      status,
      startDate,
      stopDate,
      capEnabled,
      capSeats,
      skuId,
      offerPercentage,
      // unused (the instant rewards are global, see LegacyPackSettings): sent back as stored
      rewards: drop.instantRewards.map(toRewardInput),
      schedule,
      filterUserIds,
      filterSubscriptionTier,
      filterMinCreation,
      filterMaxCreation,
      filterAppVersion,
      filterAppVersionOperator,
      filterUserLevel,
      filterExcludedUserIds,
      filterHadSub,
      filterSpendTiers,
    },
    skuOptions
  );
  const isDirty = !sameInput(currentInput, buildDropInput(drop.id, formValuesFromDrop(drop), skuOptions));

  const handleSave = () => {
    updateDrop({ variables: { input: currentInput } }).catch(() => {
      // surfaced through saveError
    });
  };

  const updateScheduleEntry = (index: number, entry: Partial<SongpopLegacyPackNotifScheduleEntryInput>) =>
    setSchedule((previous) => previous.map((current, i) => (i === index ? { ...current, ...entry } : current)));

  const fireDateOf = (entry: SongpopLegacyPackNotifScheduleEntryInput) =>
    moment(entry.anchor === SongpopLegacyPackNotifAnchor.START ? startDate : stopDate).add(
      Number(entry.offsetHours) || 0,
      'hours'
    );
  const previewFireDate = (entry: SongpopLegacyPackNotifScheduleEntryInput) =>
    fireDateOf(entry).format('YYYY-MM-DD HH:mm');
  // How many times a push fires with the dates in the form (a repeat: every N hours until the end).
  const occurrenceCountOf = (entry: SongpopLegacyPackNotifScheduleEntryInput) => {
    const every = Number(entry.repeatEveryHours) || 0;
    const first = fireDateOf(entry);
    if (every < 1 || first.isAfter(moment(stopDate))) {
      return 1;
    }
    return Math.floor(moment(stopDate).diff(first, 'hours', true) / every) + 1;
  };
  // Why "Send now" is off for push `index`, null = available. It sends the SAVED push of a running event.
  const forceSendBlocker = (index: number): string | null => {
    const now = moment();
    if (drop.status !== ChainedOfferEventStatus.ACTIVE || now.isBefore(drop.startDate) || now.isAfter(drop.stopDate)) {
      return 'Only while the event is running (ACTIVE and inside its dates)';
    }
    if (isDirty || index >= drop.notifSchedule.length) {
      return 'Save the event first';
    }
    return null;
  };
  // only a running event sends its pushes
  const isInsideEvent = (entry: SongpopLegacyPackNotifScheduleEntryInput) =>
    !fireDateOf(entry).isBefore(moment(startDate)) && !fireDateOf(entry).isAfter(moment(stopDate));

  return (
    <>
      <Container className="pb-4">
        <Row className="mb-3 align-items-center">
          <Col>
            <h4 className="mb-0">
              {drop.name}{' '}
              <Badge pill variant={statusBadge.variant} className="align-middle">
                {statusBadge.label}
              </Badge>
            </h4>
          </Col>
          <Col xs="auto" className="d-flex justify-content-end">
            <Button
              className="mr-2"
              variant="danger"
              disabled={deleting || drop.seatsSold > 0}
              title={drop.seatsSold > 0 ? 'Something was sold through this sale config: set it to DRAFT instead' : ''}
              onClick={() => setShowDeleteModal(true)}
            >
              <i className="fas fa-trash mr-2" />
              Delete
            </Button>
            <Button
              variant="success"
              disabled={saving || !isDirty}
              title={isDirty ? '' : 'Nothing changed'}
              onClick={handleSave}
            >
              {saving ? <Spinner animation="border" size="sm" /> : 'Save'}
            </Button>
          </Col>
        </Row>

        {saveError && <Alert variant="danger">{saveError.message}</Alert>}
        {deleteError && <Alert variant="danger">{deleteError.message}</Alert>}
        {overlapping.length > 0 && (
          <Alert variant="warning">
            <strong>Another event is live on these dates:</strong> {overlapping.map((other) => other.name).join(', ')}.
            Only one event can be live at a time: set that one to DRAFT or move the dates, otherwise this save is
            refused.
          </Alert>
        )}

        <Section title="General">
          <Row className="mb-3">
            <Col md={3}>Id:</Col>
            <Col md={5}>
              <code>{drop.id}</code>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>Name:</Col>
            <Col md={5}>
              <Form.Control value={name} onChange={(e) => setName(e.target.value)} />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>Status:</Col>
            <Col md={3}>
              <Form.Control
                as="select"
                value={status}
                onChange={(e) => setStatus(e.target.value as ChainedOfferEventStatus)}
              >
                {Object.values(ChainedOfferEventStatus).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Control>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>Start date:</Col>
            <Col md={5}>
              <DateTimePicker
                dateFormat="YYYY-MM-DD"
                timeFormat
                // @ts-ignore
                onChange={applyChangeIfDateValid(setStartDate)}
                // @ts-ignore
                value={startDate}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>Stop date:</Col>
            <Col md={5}>
              <DateTimePicker
                dateFormat="YYYY-MM-DD"
                timeFormat
                // @ts-ignore
                onChange={applyChangeIfDateValid(setStopDate)}
                // @ts-ignore
                value={stopDate}
              />
            </Col>
          </Row>
        </Section>

        <Section title="Matchers" hint="Who sees this offer. Empty = everyone.">
          <Row className="mb-3">
            <Col md={3}>Minimum registration date:</Col>
            <Col md={5}>
              <DateTimePicker
                dateFormat="YYYY-MM-DD"
                timeFormat
                // @ts-ignore
                onChange={applyChangeIfDateValid(setFilterMinCreation)}
                // @ts-ignore
                value={filterMinCreation}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>Maximum registration date:</Col>
            <Col md={5}>
              <DateTimePicker
                dateFormat="YYYY-MM-DD"
                timeFormat
                // @ts-ignore
                onChange={applyChangeIfDateValid(setFilterMaxCreation)}
                // @ts-ignore
                value={filterMaxCreation}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>App version:</Col>
            <Col md={2}>
              <Form.Control
                as="select"
                value={filterAppVersionOperator}
                onChange={(e) => setFilterAppVersionOperator(e.target.value)}
              >
                {APP_VERSION_OPERATORS.map((operator) => (
                  <option key={operator} value={operator}>
                    {operator}
                  </option>
                ))}
              </Form.Control>
            </Col>
            <Col md={3}>
              <Form.Control
                placeholder="any"
                value={filterAppVersion}
                onChange={(e) => setFilterAppVersion(e.target.value)}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>Min user level:</Col>
            <Col md={3}>
              <Form.Control
                placeholder="any"
                value={filterUserLevel}
                onChange={(e) => setFilterUserLevel(e.target.value)}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>Subscription tier:</Col>
            <Col md={3}>
              <Form.Control
                as="select"
                value={filterSubscriptionTier}
                onChange={(e) => setFilterSubscriptionTier(e.target.value)}
              >
                <option value={ALL_TIERS}>All</option>
                {Object.values(UserSubscriptionTier).map((tier) => (
                  <option key={tier} value={tier}>
                    {tier}
                  </option>
                ))}
              </Form.Control>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>
              User ids:
              <br />
              (separated by commas)
            </Col>
            <Col md={8}>
              <Form.Control as="textarea" value={filterUserIds} onChange={(e) => setFilterUserIds(e.target.value)} />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>Never subscribed:</Col>
            <Col md={5}>
              <Form.Check
                type="checkbox"
                id={`legacy-drop-had-sub-${drop.id}`}
                checked={filterHadSub}
                onChange={(e) => setFilterHadSub(e.target.checked)}
                label={<small className="text-muted">only users who never bought a subscription</small>}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>Not an owner yet:</Col>
            <Col md={8}>
              {/* Display only: the backend never offers the pack to an owner (getVisibleLegacyPackOffer) */}
              <Form.Check
                type="checkbox"
                id={`legacy-drop-not-owner-${drop.id}`}
                checked
                disabled
                label={
                  <small className="text-muted">
                    only users who don&apos;t own Songpop Maestro yet (always on, owners never see the offer or the
                    pushes)
                  </small>
                }
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>
              Spend Tier (PAU status):
              <br />
              <small className="text-muted">Lifetime spend, refreshed daily</small>
            </Col>
            <Col md={8}>
              <Form.Check
                type="radio"
                id={`legacy-drop-spend-tier-all-${drop.id}`}
                checked={filterSpendTiers.length === 0}
                onChange={() => setFilterSpendTiers([])}
                label={
                  <>
                    All users <small className="text-muted">no spend filter</small>
                  </>
                }
              />
              {SPEND_TIER_OPTIONS.map(({ tier, label, range }) => (
                <Form.Check
                  key={tier}
                  type="checkbox"
                  id={`legacy-drop-spend-tier-${tier}-${drop.id}`}
                  checked={filterSpendTiers.includes(tier)}
                  onChange={() => toggleSpendTier(tier)}
                  label={
                    <>
                      {label} <small className="text-muted">{range}</small>
                      {spendTierAudienceByTier[tier] && (
                        <small className="ml-2">
                          {spendTierAudienceByTier[tier].totalUsers.toLocaleString()} users
                        </small>
                      )}
                    </>
                  }
                />
              ))}
              {allSpendTiersSelected && (
                <small className="text-muted">
                  Every tier selected - this saves as <strong>All users</strong> (no spend filter), which also reaches
                  payers whose tier has not been synced yet.
                </small>
              )}
              <div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="mt-2"
                  disabled={spendTierAudienceLoading}
                  onClick={() =>
                    loadSpendTierAudience({
                      variables: {
                        filters: {
                          userIds: textToUserIds(filterUserIds),
                          excludedUserIds: textToUserIds(filterExcludedUserIds),
                          userLevel: optionalNumber(filterUserLevel) ?? undefined,
                          userMinCreationDate: filterMinCreation,
                          userMaxCreationDate: filterMaxCreation,
                          noSubscriptionBefore: filterHadSub,
                        },
                      },
                    })
                  }
                >
                  {spendTierAudienceLoading ? 'Estimating…' : 'Estimate audience'}
                </Button>
              </div>
              {spendTierComputedAt && (
                <small className="text-muted">
                  Tiers computed {new Date(spendTierComputedAt).toLocaleString()} - lifetime spend, refreshed daily (can
                  be up to 24h behind). Counts every payer that matches this sale config&apos;s other matchers too,
                  whenever they last played. NPAU is every user without a tier row, so it is not counted here.
                  {audienceIgnoredMatchers.length > 0 && (
                    <strong>
                      {' '}
                      Not accounted for: {audienceIgnoredMatchers.join(' and ')} - the real audience is smaller.
                    </strong>
                  )}
                </small>
              )}
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>
              Excluded user ids:
              <br />
              (separated by commas)
              <br />
              <small className="text-muted">Always wins over the matchers above</small>
            </Col>
            <Col md={8}>
              <Form.Control
                as="textarea"
                value={filterExcludedUserIds}
                onChange={(e) => setFilterExcludedUserIds(e.target.value)}
              />
            </Col>
          </Row>
        </Section>

        <Section title="Price and seats" hint="What the player pays, and how many seats are offered.">
          <Row className="mb-3">
            <Col md={3}>Price point:</Col>
            <Col md={6}>
              <Form.Control
                as="select"
                value={skuId}
                isInvalid={!selectedSku}
                onChange={(e) => setSkuId(e.target.value)}
              >
                {/* A price point no longer in the backend list: shown as is, instead of the browser
                  silently displaying the first option while the form keeps the unknown id. */}
                {!selectedSku && (
                  <option value={skuId} disabled>
                    {skuId} - unknown price point, pick one
                  </option>
                )}
                {skuOptions
                  .filter((option) => !option.retired || option.id === drop.skuId)
                  .map((option) => (
                    <option key={option.id} value={option.id}>
                      {skuOptionLabel(option)}
                    </option>
                  ))}
              </Form.Control>
              {!selectedSku && (
                <Form.Control.Feedback type="invalid">
                  Price point {skuId} is not in the backend list: pick one to see the prices.
                </Form.Control.Feedback>
              )}
              {selectedSku && (
                <Form.Text className="text-muted">
                  iOS <code>{selectedSku.iosSku}</code> · Android <code>{selectedSku.androidSku}</code>
                </Form.Text>
              )}
              {selectedSku && (selectedSku.iosPlaceholder || selectedSku.androidPlaceholder) && (
                <Form.Text className="text-warning">
                  The store product does not exist yet on{' '}
                  {[selectedSku.iosPlaceholder && 'iOS', selectedSku.androidPlaceholder && 'Android']
                    .filter(Boolean)
                    .join(' and ')}
                  : players there are not offered this sale config.
                </Form.Text>
              )}
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Label className="font-weight-bold">Discount</Form.Label>
              <Form.Control as="select" value={discount} onChange={(e) => setOfferPercentage(e.target.value)}>
                {DISCOUNT_OPTIONS.map(({ name: label, value }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Form.Control>
            </Col>
            <Col md={3}>
              <Form.Label className="font-weight-bold">Old price</Form.Label>
              <p>$ {oldPrice ?? '--'}</p>
            </Col>
            <Col md={3}>
              <Form.Label className="font-weight-bold">New Price</Form.Label>
              <p>$ {selectedSku?.cost ?? '--'}</p>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>Seat cap:</Col>
            <Col md={2} className="d-flex align-items-center">
              <Form.Check
                type="switch"
                id={`legacy-drop-cap-enabled-${drop.id}`}
                label={capEnabled ? 'On' : 'Off - no limit'}
                checked={capEnabled}
                onChange={(e) => setCapEnabled(e.target.checked)}
              />
            </Col>
            <Col md={2}>
              <Form.Control
                type="number"
                min={0}
                value={capSeats}
                disabled={!capEnabled}
                onChange={(e) => setCapSeats(e.target.value)}
              />
            </Col>
            <Col md={4} className="d-flex align-items-center">
              Sold: {drop.seatsSold}
              {drop.capEnabled && drop.seatsSold > drop.capSeats && (
                <span className="text-muted ml-2">(oversell, accepted)</span>
              )}
            </Col>
          </Row>
        </Section>

        <Section title="Push notifications">
          <p className="text-muted">
            Sent only to players who have not bought Songpop Maestro. Each row is one push; save the event to apply
            changes.
          </p>
          <Table size="sm">
            <thead>
              <tr>
                <th>When to send</th>
                <th>Message</th>
                <th>Who receives it</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {schedule.map((entry, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={index}>
                  <td style={{ minWidth: 300 }}>
                    <div className="d-flex align-items-center">
                      <Form.Control
                        type="number"
                        min={0}
                        style={{ width: 80 }}
                        value={Math.abs(Number(entry.offsetHours) || 0)}
                        onChange={(e) => updateScheduleEntry(index, pushTiming(entry.anchor, e.target.value))}
                      />
                      <span className="mx-2">hours</span>
                      <Form.Control
                        as="select"
                        value={entry.anchor}
                        onChange={(e) =>
                          updateScheduleEntry(
                            index,
                            pushTiming(e.target.value as SongpopLegacyPackNotifAnchor, entry.offsetHours)
                          )
                        }
                      >
                        <option value={SongpopLegacyPackNotifAnchor.START}>after the event starts</option>
                        <option value={SongpopLegacyPackNotifAnchor.STOP}>before the event ends</option>
                      </Form.Control>
                    </div>
                    <div className="d-flex align-items-center mt-1">
                      <Form.Control
                        as="select"
                        size="sm"
                        style={{ width: 150 }}
                        value={entry.repeatEveryHours ? 'repeat' : 'once'}
                        onChange={(e) =>
                          updateScheduleEntry(index, { repeatEveryHours: e.target.value === 'repeat' ? 24 : null })
                        }
                      >
                        <option value="once">send once</option>
                        <option value="repeat">then repeat every</option>
                      </Form.Control>
                      {!!entry.repeatEveryHours && (
                        <>
                          <Form.Control
                            type="number"
                            size="sm"
                            min={1}
                            step={1}
                            className="ml-2"
                            style={{ width: 70 }}
                            value={entry.repeatEveryHours}
                            onChange={(e) =>
                              updateScheduleEntry(index, {
                                // whole hours, at least 1 (same rule as the server)
                                repeatEveryHours: Math.max(1, Math.round(Number(e.target.value)) || 1),
                              })
                            }
                          />
                          <span className="ml-2 small">hours until the end</span>
                        </>
                      )}
                    </div>
                    <small className="text-muted">
                      <i className="far fa-clock mr-1" />
                      {entry.repeatEveryHours
                        ? `first ${previewFireDate(entry)}, ${occurrenceCountOf(entry)} times in total`
                        : previewFireDate(entry)}
                    </small>
                    {!isInsideEvent(entry) && (
                      <small className="d-block text-danger">Outside the event dates: this push is never sent.</small>
                    )}
                  </td>
                  <td style={{ minWidth: 280 }}>
                    <Form.Control
                      className="mb-1"
                      placeholder={`Title (empty = default "${defaultTextName(entry)}" title)`}
                      maxLength={MAX_NOTIF_TITLE_LENGTH}
                      value={entry.title ?? ''}
                      onChange={(e) => updateScheduleEntry(index, { title: e.target.value })}
                    />
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder={`Text (empty = default "${defaultTextName(entry)}" text)`}
                      maxLength={MAX_NOTIF_BODY_LENGTH}
                      value={entry.body ?? ''}
                      onChange={(e) => updateScheduleEntry(index, { body: e.target.value })}
                    />
                  </td>
                  <td style={{ minWidth: 160 }}>
                    <small className="d-block mb-1">{pushAudienceSummary(entry)}</small>
                    <Button variant="outline-primary" size="sm" onClick={() => setAudiencePushIndex(index)}>
                      <i className="fas fa-users mr-1" />
                      Change
                    </Button>
                  </td>
                  <td>
                    <PushStatus stored={drop.notifSchedule[index]} />
                  </td>
                  <td className="text-nowrap">
                    <Button
                      variant="outline-warning"
                      size="sm"
                      className="mr-1"
                      disabled={forceSending || !!forceSendBlocker(index)}
                      title={forceSendBlocker(index) ?? 'Send this push now, without waiting for its time'}
                      onClick={() => setForcePushIndex(index)}
                    >
                      <i className="fas fa-paper-plane mr-1" />
                      Send now
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => setSchedule((previous) => previous.filter((__, i) => i !== index))}
                    >
                      <i className="fas fa-trash" />
                    </Button>
                    {forcedPushIndex === index && !forceSendError && (
                      <small className="d-block text-success">Sending started</small>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {forceSendError && <Alert variant="danger">{forceSendError.message}</Alert>}
          <Button
            variant="outline-success"
            size="sm"
            onClick={() =>
              setSchedule((previous) => [...previous, { anchor: SongpopLegacyPackNotifAnchor.START, offsetHours: 0 }])
            }
          >
            <i className="fas fa-plus mr-2" />
            Add push
          </Button>
          <PushAudienceModal
            show={audiencePushIndex !== null}
            entry={audiencePushIndex !== null ? schedule[audiencePushIndex] ?? null : null}
            onHide={() => setAudiencePushIndex(null)}
            onApply={(filters) => {
              if (audiencePushIndex !== null) {
                // the matchers replace the push's previous ones (a cleared one is dropped, not kept)
                setSchedule((previous) =>
                  previous.map((current, i) =>
                    i === audiencePushIndex
                      ? {
                          anchor: current.anchor,
                          offsetHours: current.offsetHours,
                          title: current.title,
                          body: current.body,
                          repeatEveryHours: current.repeatEveryHours,
                          ...filters,
                        }
                      : current
                  )
                );
              }
              setAudiencePushIndex(null);
            }}
          />
        </Section>

        {/* Floating save: always reachable without scrolling back to the top bar. */}
        <div
          className="d-flex justify-content-end align-items-center bg-white border-top py-2 px-3 shadow"
          style={{ position: 'sticky', bottom: 0, zIndex: 10 }}
        >
          {saveError && <span className="text-danger mr-3 text-truncate">{saveError.message}</span>}
          <Button
            variant="success"
            disabled={saving || !isDirty}
            title={isDirty ? '' : 'Nothing changed'}
            onClick={handleSave}
          >
            {saving ? <Spinner animation="border" size="sm" /> : 'Save'}
          </Button>
        </div>
      </Container>

      <Modal show={forcePushIndex !== null} onHide={() => setForcePushIndex(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Send push {forcePushIndex !== null ? forcePushIndex + 1 : ''} now?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {forcePushIndex !== null && drop.notifSchedule[forcePushIndex] && (
            <>
              <p>
                Goes out right away to: <b>{pushAudienceSummary(drop.notifSchedule[forcePushIndex])}</b> (offline
                players who have not bought Songpop Maestro).
              </p>
              <p className="mb-0 text-muted">
                {drop.notifSchedule[forcePushIndex].repeatEveryHours
                  ? 'Its repeats keep their schedule.'
                  : 'It will not be sent again at its scheduled time.'}{' '}
                This cannot be undone.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setForcePushIndex(null)}>
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={() => {
              const index = forcePushIndex;
              setForcePushIndex(null);
              if (index === null) {
                return;
              }
              setForcedPushIndex(null);
              forceSendPush({ variables: { dropId: drop.id, index } })
                .then(() => setForcedPushIndex(index))
                .catch(() => {
                  // surfaced through forceSendError
                });
            }}
          >
            Send now
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete sale config</Modal.Title>
        </Modal.Header>
        <Modal.Body>This cannot be undone. Delete the sale config &quot;{drop.name}&quot;?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              setShowDeleteModal(false);
              deleteDrop({ variables: { id: drop.id } }).catch(() => {
                // surfaced through deleteError
              });
            }}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
